export const AJU_SEGMENT_LENGTHS = [6, 6, 8, 6] as const;
export const AJU_MAX_RAW_LENGTH = 26;

export type AjuSegments = [string, string, string, string];

export function cleanAjuRaw(value: string): string {
  return value.replace(/[^0-9a-zA-Z]/g, '').toUpperCase();
}

export function splitAjuToSegments(raw: string): AjuSegments {
  const clean = cleanAjuRaw(raw);
  let offset = 0;

  return AJU_SEGMENT_LENGTHS.map((length) => {
    const segment = clean.slice(offset, offset + length);
    offset += length;
    return segment;
  }) as AjuSegments;
}

export function joinAjuSegments(segments: AjuSegments): string {
  return segments.join('');
}

/** @deprecated Use formatAjuNumber instead */
export function ajuFormatter(input: string | null | undefined): string {
  if (!input || input.length < 22) {
    return '';
  }

  return formatAjuNumber(input);
}

export function formatAjuNumber(raw: string): string {
  return formatAjuSegments(splitAjuToSegments(raw));
}

function getFormattedSegmentLayout(segments: AjuSegments): Array<{ segmentIndex: number; formattedStart: number }> {
  const layout: Array<{ segmentIndex: number; formattedStart: number }> = [];
  let formattedPos = 0;

  for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
    const segment = segments[segmentIndex];
    if (segment.length === 0) {
      continue;
    }

    layout.push({ segmentIndex, formattedStart: formattedPos });
    formattedPos += segment.length;

    if (segmentIndex < segments.length - 1 && segments.slice(segmentIndex + 1).some((part) => part.length > 0)) {
      formattedPos += 1;
    }
  }

  return layout;
}

export function formatAjuSegments(segments: AjuSegments): string {
  if (!segments.some((segment) => segment.length > 0)) {
    return '';
  }

  const parts: string[] = [];

  for (const segment of segments) {
    if (segment.length > 0) {
      parts.push(segment);
    }
  }

  return parts.join('-');
}

export function getSegmentInfoFromFormattedPos(
  formattedPos: number,
  segments: AjuSegments
): { segmentIndex: number; offsetInSegment: number } {
  const layout = getFormattedSegmentLayout(segments);

  for (let index = 0; index < layout.length; index++) {
    const { segmentIndex, formattedStart } = layout[index];
    const segment = segments[segmentIndex];
    const formattedEnd = formattedStart + segment.length;

    if (formattedPos <= formattedEnd) {
      return {
        segmentIndex,
        offsetInSegment: Math.max(0, Math.min(formattedPos - formattedStart, segment.length)),
      };
    }
  }

  const lastLayout = layout.at(-1);
  if (lastLayout) {
    return {
      segmentIndex: lastLayout.segmentIndex,
      offsetInSegment: segments[lastLayout.segmentIndex].length,
    };
  }

  return { segmentIndex: 0, offsetInSegment: 0 };
}

export function getFormattedPosFromSegmentOffset(
  segments: AjuSegments,
  segmentIndex: number,
  offsetInSegment: number
): number {
  const layout = getFormattedSegmentLayout(segments);
  const entry = layout.find((item) => item.segmentIndex === segmentIndex);

  if (!entry) {
    const nextVisible = layout.find((item) => item.segmentIndex >= segmentIndex);
    if (nextVisible) {
      return nextVisible.formattedStart;
    }

    return formatAjuSegments(segments).length;
  }

  return entry.formattedStart + offsetInSegment;
}

export function applyAjuDeletionToSegments(
  segments: AjuSegments,
  formattedSelectionStart: number,
  formattedSelectionEnd: number,
  isBackspace: boolean
): { segments: AjuSegments; cursorFormattedPos: number; segmentIndex: number; offsetInSegment: number } {
  const nextSegments: AjuSegments = [...segments];
  const startInfo = getSegmentInfoFromFormattedPos(formattedSelectionStart, segments);
  const endInfo = getSegmentInfoFromFormattedPos(formattedSelectionEnd, segments);
  const segmentIndex = startInfo.segmentIndex;
  const segment = nextSegments[segmentIndex];

  const offsetStart = Math.max(0, Math.min(startInfo.offsetInSegment, segment.length));
  const offsetEnd =
    endInfo.segmentIndex === segmentIndex
      ? Math.max(offsetStart, Math.min(endInfo.offsetInSegment, segment.length))
      : segment.length;

  let nextOffset = offsetStart;

  if (formattedSelectionStart !== formattedSelectionEnd) {
    if (startInfo.segmentIndex === endInfo.segmentIndex) {
      nextSegments[segmentIndex] = segment.slice(0, offsetStart) + segment.slice(offsetEnd);
      nextOffset = offsetStart;
    } else {
      nextSegments[startInfo.segmentIndex] = segment.slice(0, offsetStart);

      for (let index = startInfo.segmentIndex + 1; index < endInfo.segmentIndex; index++) {
        nextSegments[index] = '';
      }

      const endSegment = nextSegments[endInfo.segmentIndex];
      const endOffset = Math.max(0, Math.min(endInfo.offsetInSegment, endSegment.length));
      nextSegments[endInfo.segmentIndex] = endSegment.slice(endOffset);
      nextOffset = nextSegments[startInfo.segmentIndex].length;
    }
  } else if (isBackspace) {
    if (offsetStart > 0) {
      nextSegments[segmentIndex] = segment.slice(0, offsetStart - 1) + segment.slice(offsetStart);
      nextOffset = offsetStart - 1;
    }
  } else if (offsetStart < segment.length) {
    nextSegments[segmentIndex] = segment.slice(0, offsetStart) + segment.slice(offsetStart + 1);
    nextOffset = offsetStart;
  }

  return {
    segments: nextSegments,
    cursorFormattedPos: getFormattedPosFromSegmentOffset(nextSegments, segmentIndex, nextOffset),
    segmentIndex,
    offsetInSegment: nextOffset,
  };
}

export function applyAjuInsertionAtSegmentOffset(
  segments: AjuSegments,
  segmentIndex: number,
  offsetInSegment: number,
  text: string
): { segments: AjuSegments; cursorFormattedPos: number } | null {
  const cleanText = cleanAjuRaw(text);
  if (!cleanText) {
    return null;
  }

  const nextSegments: AjuSegments = [...segments];
  const maxLength = AJU_SEGMENT_LENGTHS[segmentIndex];
  const segment = nextSegments[segmentIndex];
  const availableSpace = maxLength - segment.length;

  if (availableSpace <= 0) {
    return null;
  }

  const insertedText = cleanText.slice(0, availableSpace);
  nextSegments[segmentIndex] = segment.slice(0, offsetInSegment) + insertedText + segment.slice(offsetInSegment);

  return {
    segments: nextSegments,
    cursorFormattedPos: getFormattedPosFromSegmentOffset(
      nextSegments,
      segmentIndex,
      offsetInSegment + insertedText.length
    ),
  };
}

export function resolveWritableSegmentPosition(
  segments: AjuSegments,
  formattedPos: number
): { segmentIndex: number; offsetInSegment: number } {
  let { segmentIndex, offsetInSegment } = getSegmentInfoFromFormattedPos(formattedPos, segments);

  while (segmentIndex < AJU_SEGMENT_LENGTHS.length) {
    const maxLength = AJU_SEGMENT_LENGTHS[segmentIndex];
    const segment = segments[segmentIndex];
    const clampedOffset = Math.max(0, Math.min(offsetInSegment, segment.length));

    if (segment.length < maxLength) {
      return { segmentIndex, offsetInSegment: clampedOffset };
    }

    if (clampedOffset < segment.length) {
      return { segmentIndex, offsetInSegment: clampedOffset };
    }

    segmentIndex += 1;
    offsetInSegment = 0;
  }

  const lastSegmentIndex = AJU_SEGMENT_LENGTHS.length - 1;

  return {
    segmentIndex: lastSegmentIndex,
    offsetInSegment: segments[lastSegmentIndex].length,
  };
}

export function applyAjuInsertionToSegments(
  segments: AjuSegments,
  formattedCursorPos: number,
  text: string
): { segments: AjuSegments; cursorFormattedPos: number } | null {
  const { segmentIndex, offsetInSegment } = resolveWritableSegmentPosition(segments, formattedCursorPos);
  return applyAjuInsertionAtSegmentOffset(segments, segmentIndex, offsetInSegment, text);
}

export function applyAjuPasteToSegments(
  segments: AjuSegments,
  formattedSelectionStart: number,
  formattedSelectionEnd: number,
  pastedText: string
): { segments: AjuSegments; cursorFormattedPos: number } {
  const cleanText = cleanAjuRaw(pastedText);

  let workingSegments = segments;
  let segmentIndex = getSegmentInfoFromFormattedPos(formattedSelectionStart, segments).segmentIndex;
  let offsetInSegment = getSegmentInfoFromFormattedPos(formattedSelectionStart, segments).offsetInSegment;

  if (formattedSelectionStart !== formattedSelectionEnd) {
    const deleteResult = applyAjuDeletionToSegments(
      workingSegments,
      formattedSelectionStart,
      formattedSelectionEnd,
      false
    );
    workingSegments = deleteResult.segments;
    segmentIndex = deleteResult.segmentIndex;
    offsetInSegment = deleteResult.offsetInSegment;
  }

  if (!cleanText) {
    return {
      segments: workingSegments,
      cursorFormattedPos: getFormattedPosFromSegmentOffset(workingSegments, segmentIndex, offsetInSegment),
    };
  }

  let remaining = cleanText;
  const nextSegments: AjuSegments = [...workingSegments];

  while (remaining.length > 0 && segmentIndex < AJU_SEGMENT_LENGTHS.length) {
    const maxLength = AJU_SEGMENT_LENGTHS[segmentIndex];
    const segment = nextSegments[segmentIndex];
    const clampedOffset = Math.max(0, Math.min(offsetInSegment, segment.length));
    const availableSpace = maxLength - segment.length;
    const replaceableSpace = segment.length - clampedOffset;

    if (availableSpace > 0) {
      const chunk = remaining.slice(0, availableSpace);
      nextSegments[segmentIndex] = segment.slice(0, clampedOffset) + chunk + segment.slice(clampedOffset);
      remaining = remaining.slice(chunk.length);
      offsetInSegment = clampedOffset + chunk.length;
    } else if (replaceableSpace > 0) {
      const chunk = remaining.slice(0, replaceableSpace);
      nextSegments[segmentIndex] =
        segment.slice(0, clampedOffset) + chunk + segment.slice(clampedOffset + chunk.length);
      remaining = remaining.slice(chunk.length);
      offsetInSegment = clampedOffset + chunk.length;
    } else {
      segmentIndex += 1;
      offsetInSegment = 0;
      continue;
    }

    if (offsetInSegment >= maxLength) {
      segmentIndex += 1;
      offsetInSegment = 0;
    }
  }

  const cursorSegmentIndex = Math.min(segmentIndex, AJU_SEGMENT_LENGTHS.length - 1);

  return {
    segments: nextSegments,
    cursorFormattedPos: getFormattedPosFromSegmentOffset(nextSegments, cursorSegmentIndex, offsetInSegment),
  };
}

export function applyAjuInputChangeToSegments(
  segments: AjuSegments,
  newFormattedValue: string
): { segments: AjuSegments; cursorFormattedPos: number } {
  const prevFormatted = formatAjuSegments(segments);
  const prevClean = cleanAjuRaw(prevFormatted);
  const newClean = cleanAjuRaw(newFormattedValue);

  if (newClean === prevClean && newFormattedValue === prevFormatted) {
    return { segments, cursorFormattedPos: prevFormatted.length };
  }

  let prefixLen = 0;
  while (
    prefixLen < prevFormatted.length &&
    prefixLen < newFormattedValue.length &&
    prevFormatted[prefixLen] === newFormattedValue[prefixLen]
  ) {
    prefixLen += 1;
  }

  let prevSuffix = prevFormatted.length;
  let newSuffix = newFormattedValue.length;
  while (
    prevSuffix > prefixLen &&
    newSuffix > prefixLen &&
    prevFormatted[prevSuffix - 1] === newFormattedValue[newSuffix - 1]
  ) {
    prevSuffix -= 1;
    newSuffix -= 1;
  }

  const insertedText = cleanAjuRaw(newFormattedValue.slice(prefixLen, newSuffix));

  let workingSegments = segments;
  let targetSegmentIndex: number;
  let targetOffsetInSegment: number;

  if (prevSuffix > prefixLen) {
    const deleteResult = applyAjuDeletionToSegments(workingSegments, prefixLen, prevSuffix, false);
    workingSegments = deleteResult.segments;
    targetSegmentIndex = deleteResult.segmentIndex;
    targetOffsetInSegment = deleteResult.offsetInSegment;
  } else {
    const insertTarget = resolveWritableSegmentPosition(segments, prefixLen);
    targetSegmentIndex = insertTarget.segmentIndex;
    targetOffsetInSegment = insertTarget.offsetInSegment;
  }

  if (insertedText.length > 0) {
    const insertResult = applyAjuInsertionAtSegmentOffset(
      workingSegments,
      targetSegmentIndex,
      targetOffsetInSegment,
      insertedText
    );
    if (insertResult) {
      return insertResult;
    }
  }

  return {
    segments: workingSegments,
    cursorFormattedPos: getFormattedPosFromSegmentOffset(workingSegments, targetSegmentIndex, targetOffsetInSegment),
  };
}
